const FORM_SELECTOR = 'form[action="/cart/add"]';
const ADD_BUTTON_SELECTOR = 'button[name="add"]';

function clampPositiveQuantity(value) {
  const parsed = Number.parseInt(String(value), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

function buildCartAddItemsWithExtras({
  mainVariantId,
  mainQuantity,
  mainSellingPlanId,
  extraItems = []
}) {
  const mainItem = {
    id: Number.parseInt(String(mainVariantId), 10),
    quantity: clampPositiveQuantity(mainQuantity)
  };
  if (Number.isFinite(Number(mainSellingPlanId)) && String(mainSellingPlanId).trim() !== "") {
    mainItem.selling_plan = Number.parseInt(String(mainSellingPlanId), 10);
  }
  return [
    mainItem,
    ...extraItems
      .map((extra) => ({
        id: Number.parseInt(String(extra?.id), 10),
        quantity: clampPositiveQuantity(extra?.quantity)
      }))
      .filter((extra) => Number.isFinite(extra.id) && extra.id > 0)
  ];
}

function findMainQuantityInput(form) {
  const direct = form.querySelector('input[name="quantity"]');
  if (direct) return direct;
  const formId = form.getAttribute("id");
  return formId ? document.querySelector(`input[name="quantity"][form="${formId}"]`) : null;
}

function findMainSellingPlanInput(form) {
  const direct = form.querySelector('input[name="selling_plan"]');
  if (direct) return direct;
  const formId = form.getAttribute("id");
  return formId ? document.querySelector(`input[name="selling_plan"][form="${formId}"]`) : null;
}

function setButtonSubmitting(button, isSubmitting, originalText, addingText) {
  if (!button) return;
  button.disabled = isSubmitting;
  button.textContent = isSubmitting ? addingText : originalText;
}

export function initNFillAccessoryAddon(root) {
  if (!root || root.dataset.initialized === "true") return root;
  root.dataset.initialized = "true";

  const options = Array.from(root.querySelectorAll("[data-nfill-addon-item]"));
  const missingVariantMessage = root.dataset.errorMissingVariant || "Please select a product option.";
  const addToCartErrorMessage =
    root.dataset.errorAddToCart || "We couldn’t add this item to your cart. Please try again.";
  const addingText = root.dataset.addingLabel || "…";
  let isSubmitting = false;

  const selectedExtras = () =>
    options
      .filter((option) => option.classList.contains("selected"))
      .map((option) => ({
        id: option.dataset.addonVariantId,
        quantity: clampPositiveQuantity(option.querySelector("[data-addon-qty-value]")?.textContent),
        unitCents: Number.parseInt(option.dataset.addonUnitCents || "0", 10) || 0
      }));

  const dispatchChange = () => {
    const extras = selectedExtras();
    const addonTotalCents = extras.reduce(
      (sum, item) => sum + item.unitCents * item.quantity,
      0
    );
    document.dispatchEvent(
      new CustomEvent("nd:nfill-addon-change", {
        detail: {
          productHandle: root.dataset.productHandle || "",
          addonTotalCents,
          selectedVariantIds: extras.map((item) => item.id)
        }
      })
    );
  };

  const setSelected = (option, selected) => {
    option.classList.toggle("selected", selected);
    option.setAttribute("aria-checked", selected ? "true" : "false");
    dispatchChange();
  };

  const buildItems = (form) => {
    const mainVariantInput = form.querySelector('input[name="id"]');
    if (!mainVariantInput) throw new Error(missingVariantMessage);
    const mainQuantityInput = findMainQuantityInput(form);
    const mainSellingPlanInput = findMainSellingPlanInput(form);
    return buildCartAddItemsWithExtras({
      mainVariantId: mainVariantInput.value,
      mainQuantity: mainQuantityInput?.value || 1,
      mainSellingPlanId: mainSellingPlanInput?.value || "",
      extraItems: selectedExtras()
    });
  };

  const openCartDrawer = async () => {
    const cartDrawer = document.querySelector("cart-drawer");
    if (!cartDrawer || typeof cartDrawer.renderContents !== "function") {
      window.location.href = "/cart";
      return;
    }
    const response = await fetch("/cart?sections=cart-drawer,cart-icon-bubble");
    const sections = await response.json();
    cartDrawer.renderContents({ sections });
    cartDrawer.classList.remove("is-empty");
  };

  const submit = async (form, button) => {
    if (isSubmitting) return;
    isSubmitting = true;
    const originalText = button?.textContent || "";
    try {
      setButtonSubmitting(button, true, originalText, addingText);
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: buildItems(form) })
      });
      if (!response.ok) {
        const payload = await response.json();
        throw new Error(payload.description || addToCartErrorMessage);
      }
      await response.json();
      await openCartDrawer();
    } catch (error) {
      console.error("N-Fill add-on cart error:", error);
      alert(error.message || addToCartErrorMessage);
    } finally {
      isSubmitting = false;
      setButtonSubmitting(button, false, originalText, addingText);
    }
  };

  document.querySelectorAll(FORM_SELECTOR).forEach((form) => {
    if (form.dataset.ndNfillAddonBound === "true") return;
    form.dataset.ndNfillAddonBound = "true";
    form.addEventListener(
      "submit",
      (event) => {
        const submitter = event.submitter;
        if (submitter && submitter.name !== "add") return;
        if (!submitter && document.activeElement?.getAttribute("name") !== "add") return;
        event.preventDefault();
        event.stopImmediatePropagation();
        submit(form, submitter || form.querySelector(ADD_BUTTON_SELECTOR));
      },
      true
    );
  });

  document.querySelectorAll(`${FORM_SELECTOR} ${ADD_BUTTON_SELECTOR}`).forEach((button) => {
    if (button.dataset.ndNfillAddonBound === "true") return;
    button.dataset.ndNfillAddonBound = "true";
    button.addEventListener(
      "click",
      (event) => {
        const form = button.form || button.closest(FORM_SELECTOR);
        if (!form) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        submit(form, button);
      },
      true
    );
  });

  options.forEach((option) => {
    option.addEventListener("click", (event) => {
      if (event.target.closest("[data-addon-qty-button]")) return;
      setSelected(option, !option.classList.contains("selected"));
    });
    option.addEventListener("keydown", (event) => {
      if (!["Enter", " "].includes(event.key)) return;
      event.preventDefault();
      setSelected(option, !option.classList.contains("selected"));
    });
    option.querySelectorAll("[data-addon-qty-button]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const value = option.querySelector("[data-addon-qty-value]");
        const current = clampPositiveQuantity(value?.textContent);
        value.textContent = String(
          button.dataset.addonQtyButton === "+" ? current + 1 : Math.max(1, current - 1)
        );
        dispatchChange();
      });
    });
  });

  dispatchChange();
  return root;
}

function autoInit() {
  document.querySelectorAll("[data-nd-nfill-addon]").forEach((root) => {
    initNFillAccessoryAddon(root);
  });
}

if (typeof window !== "undefined") {
  window.NDStorefront = window.NDStorefront || {};
  window.NDStorefront.initNFillAccessoryAddon = initNFillAccessoryAddon;
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", autoInit, { once: true });
  } else {
    autoInit();
  }
}
